import { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { IProvince, IRegency, IDistrict, IVillage, IRegionResponse } from '@/types/region.types';

interface UseRegionCascadeReturn {
  // Data
  provinces: IProvince[];
  regencies: IRegency[];
  districts: IDistrict[];
  villages: IVillage[];

  // Loading states
  isLoadingProvinces: boolean;
  isLoadingRegencies: boolean;
  isLoadingDistricts: boolean;
  isLoadingVillages: boolean;

  /**
   * True when the last fetch for that level failed. The lists themselves go
   * empty on failure, which on its own is indistinguishable from "this province
   * genuinely has no entries" — the caller needs this to say so and offer a
   * retry instead of showing a silently empty dropdown.
   */
  provincesError: boolean;
  regenciesError: boolean;
  districtsError: boolean;
  villagesError: boolean;

  /** Re-runs the fetches for whichever levels are currently selected. */
  retry: () => void;

  // Selected values
  selectedProvince: string;
  selectedRegency: string;
  selectedDistrict: string;
  selectedVillage: string;

  // Handlers
  handleProvinceChange: (provinceId: string) => void;
  handleRegencyChange: (regencyId: string) => void;
  handleDistrictChange: (districtId: string) => void;
  handleVillageChange: (villageId: string) => void;

  /**
   * Adopts a selection produced outside the cascade — restoring an autosave
   * draft, for instance. The selects render from this hook's own state, so a
   * `form.reset()` alone leaves them blank even though the form holds the ids.
   */
  syncSelection: (provinceId?: string | null, regencyId?: string | null) => void;

  // Reset function
  reset: () => void;
}

export const useRegionCascade = (
  initialProvinceId?: string,
  initialRegencyId?: string,
  initialDistrictId?: string,
  initialVillageId?: string,
): UseRegionCascadeReturn => {
  const [provinces, setProvinces] = useState<IProvince[]>([]);
  const [regencies, setRegencies] = useState<IRegency[]>([]);
  const [districts, setDistricts] = useState<IDistrict[]>([]);
  const [villages, setVillages] = useState<IVillage[]>([]);

  const [isLoadingProvinces, setIsLoadingProvinces] = useState(false);
  const [isLoadingRegencies, setIsLoadingRegencies] = useState(false);
  const [isLoadingDistricts, setIsLoadingDistricts] = useState(false);
  const [isLoadingVillages, setIsLoadingVillages] = useState(false);

  const [provincesError, setProvincesError] = useState(false);
  const [regenciesError, setRegenciesError] = useState(false);
  const [districtsError, setDistrictsError] = useState(false);
  const [villagesError, setVillagesError] = useState(false);

  // Bumped by retry(); every loader depends on it so a failed level can be
  // fetched again without remounting the form and losing what the user typed.
  const [reloadToken, setReloadToken] = useState(0);

  const [selectedProvince, setSelectedProvince] = useState(initialProvinceId || '');
  const [selectedRegency, setSelectedRegency] = useState(initialRegencyId || '');
  const [selectedDistrict, setSelectedDistrict] = useState(initialDistrictId || '');
  const [selectedVillage, setSelectedVillage] = useState(initialVillageId || '');

  // Load provinces on mount
  useEffect(() => {
    const loadProvinces = async () => {
      setIsLoadingProvinces(true);
      setProvincesError(false);
      try {
        const { data } = await axiosInstance.get<IRegionResponse<IProvince>>(
          `${env.apiTasklistBaseUrl}/api/v1/compro/regions/provinces`,
        );
        setProvinces(data.data || []);
      } catch (error) {
        console.error('Error loading provinces:', error);
        setProvinces([]);
        setProvincesError(true);
      } finally {
        setIsLoadingProvinces(false);
      }
    };

    loadProvinces();
  }, [reloadToken]);

  // Load regencies when province changes
  useEffect(() => {
    if (!selectedProvince) {
      setRegencies([]);
      setRegenciesError(false);
      return;
    }

    const loadRegencies = async () => {
      setIsLoadingRegencies(true);
      setRegenciesError(false);
      try {
        const { data } = await axiosInstance.get<IRegionResponse<IRegency>>(
          `${env.apiTasklistBaseUrl}/api/v1/compro/regions/provinces/${selectedProvince}/regencies`,
        );
        setRegencies(data.data || []);
      } catch (error) {
        console.error('Error loading regencies:', error);
        setRegencies([]);
        setRegenciesError(true);
      } finally {
        setIsLoadingRegencies(false);
      }
    };

    loadRegencies();
  }, [selectedProvince, reloadToken]);

  // Load districts when regency changes
  useEffect(() => {
    if (!selectedRegency) {
      setDistricts([]);
      setDistrictsError(false);
      return;
    }

    const loadDistricts = async () => {
      setIsLoadingDistricts(true);
      setDistrictsError(false);
      try {
        const { data } = await axiosInstance.get<IRegionResponse<IDistrict>>(
          `${env.apiTasklistBaseUrl}/api/v1/compro/regions/regencies/${selectedRegency}/districts`,
        );
        setDistricts(data.data || []);
      } catch (error) {
        console.error('Error loading districts:', error);
        setDistricts([]);
        setDistrictsError(true);
      } finally {
        setIsLoadingDistricts(false);
      }
    };

    loadDistricts();
  }, [selectedRegency, reloadToken]);

  // Load villages when district changes
  useEffect(() => {
    if (!selectedDistrict) {
      setVillages([]);
      setVillagesError(false);
      return;
    }

    const loadVillages = async () => {
      setIsLoadingVillages(true);
      setVillagesError(false);
      try {
        const { data } = await axiosInstance.get<IRegionResponse<IVillage>>(
          `${env.apiTasklistBaseUrl}/api/v1/compro/regions/districts/${selectedDistrict}/villages`,
        );
        setVillages(data.data || []);
      } catch (error) {
        console.error('Error loading villages:', error);
        setVillages([]);
        setVillagesError(true);
      } finally {
        setIsLoadingVillages(false);
      }
    };

    loadVillages();
  }, [selectedDistrict, reloadToken]);

  const handleProvinceChange = (provinceId: string) => {
    setSelectedProvince(provinceId);
    // Reset dependent selections
    setSelectedRegency('');
    setSelectedDistrict('');
    setSelectedVillage('');
    setRegencies([]);
    setDistricts([]);
    setVillages([]);
  };

  const handleRegencyChange = (regencyId: string) => {
    setSelectedRegency(regencyId);
    // Reset dependent selections
    setSelectedDistrict('');
    setSelectedVillage('');
    setDistricts([]);
    setVillages([]);
  };

  const handleDistrictChange = (districtId: string) => {
    setSelectedDistrict(districtId);
    // Reset dependent selection
    setSelectedVillage('');
    setVillages([]);
  };

  const handleVillageChange = (villageId: string) => {
    setSelectedVillage(villageId);
  };

  const retry = useCallback(() => setReloadToken(token => token + 1), []);

  const syncSelection = useCallback((provinceId?: string | null, regencyId?: string | null) => {
    setSelectedProvince(provinceId || '');
    setSelectedRegency(regencyId || '');
    // The form only carries province and regency, so anything deeper cannot be
    // reconstructed and would otherwise be left pointing at the old province.
    setSelectedDistrict('');
    setSelectedVillage('');
    setDistricts([]);
    setVillages([]);
  }, []);

  const reset = () => {
    setSelectedProvince('');
    setSelectedRegency('');
    setSelectedDistrict('');
    setSelectedVillage('');
    setRegencies([]);
    setDistricts([]);
    setVillages([]);
  };

  return {
    provinces,
    regencies,
    districts,
    villages,
    isLoadingProvinces,
    isLoadingRegencies,
    isLoadingDistricts,
    isLoadingVillages,
    provincesError,
    regenciesError,
    districtsError,
    villagesError,
    retry,
    syncSelection,
    selectedProvince,
    selectedRegency,
    selectedDistrict,
    selectedVillage,
    handleProvinceChange,
    handleRegencyChange,
    handleDistrictChange,
    handleVillageChange,
    reset,
  };
};
